import { useEffect, useState } from 'react';
import { monthsChipsEnum } from 'enums';
import { useNavigate, useParams } from 'react-router-dom';
import { Table, TableColumn, Tooltip } from '@components';
import { pageRange } from '@components/Table/TableHelper';
import { formatCurrency } from 'utils/string/currency';
import CategoryAPICaller from '@services/api/category';
import closeIcon from '@assets/icons/close.svg';
import { Category } from 'interfaces/Category';
import ConsolidatedBudgetAPICaller from '@services/api/consolidatedBudget';

type Evolution = {
  key: string;
  month: string;
  name: string;
  category: string;
  businessPartner: string;
  totalBudget: number;
  totalRealized: number;
  percentageUsage: number;
};

export default function ConsolidatedBudgetEvolutionPage() {
  const navigate = useNavigate();
  const params = useParams();
  const [currentCategory, setCurrentCategory] = useState(1);
  const [evolutionData, setEvolutionData] = useState([] as Evolution[]);
  const [totalSummary, setTotalSummary] = useState({} as Evolution);
  const [categories, setCategories] = useState<Array<Category>>([]);

  useEffect(() => {
    if (!params.budgetTemplateId || !params.costCenterId) return;
    CategoryAPICaller.listPinned(
      params.budgetTemplateId,
      params.costCenterId
    ).then((values) => {
      setCategories(values.result);
    });
  }, [params.costCenterId, params.budgetTemplateId]);

  useEffect(() => {
    if (!categories.length) return;

    ConsolidatedBudgetAPICaller.getEvolutionData({
      budgetTemplateId: params.budgetTemplateId,
      costCenterId: params.costCenterId,
      type: params.type,
      categoryId: categories[currentCategory - 1].id,
    }).then((evolutionData) => {
      const category = categories[currentCategory - 1].name;
      setEvolutionData(
        evolutionData.months.map((data: Evolution) => ({
          ...data,
          key: data.month,
          category,
          name:
            monthsChipsEnum.find((month) => month.key === data.month)?.name ||
            '',
        }))
      );
      setTotalSummary({
        ...evolutionData.totalSummary,
        category,
        businessPartner:
          evolutionData.months
            .map((data: Evolution) => data.businessPartner)
            .filter(
              (value: string, index: number, array: string[]) =>
                array.indexOf(value) === index && value !== ' - '
            )
            .join(', ') || ' - ',
      });
    });
  }, [params, categories, currentCategory]);

  const getPercentageClass = (percentage: number) => {
    if (percentage > 0) {
      return 'text-negative font-s-14px font-weight-bold';
    }

    if (percentage < 0) {
      return 'text-positive font-s-14px font-weight-bold';
    }

    return 'font-weight-bold font-s-14px text-neutral-40';
  };

  const pages = pageRange(currentCategory, categories.length, 16);

  return (
    <>
      <div className="p-s-300">
        <div className="d-flex mb-s-250">
          <h1 className="col-sm-8 color-primary">Evolução de budget mensal</h1>
          <img
            className="ml-auto pointer"
            onClick={() => navigate(-1)}
            alt="Fechar"
            src={closeIcon}
          />
        </div>

        {(categories.length && (
          <div className="mt-s-200">
            <Table
              dataKey="key"
              className="mt-s-200 mb-s-200"
              data={evolutionData}
              showColumnSelector={false}
            >
              <TableColumn
                fromKey="name"
                header="Mês"
                disableSorting
                width="25% !important"
              />
              <TableColumn
                fromKey="category"
                header="Categoria"
                disableSorting
                width="25% !important"
              />
              <TableColumn
                fromKey="businessPartner"
                header="Fornecedor"
                disableSorting
                width="25% !important"
              />
              <TableColumn
                fromKey="totalBudget"
                header="Previsto"
                disableSorting
                width="25% !important"
                render={(value) => {
                  return formatCurrency(Number(value));
                }}
              />
              <TableColumn
                fromKey="totalRealized"
                header="Realizado"
                disableSorting
                width="25% !important"
                render={(value) => {
                  return formatCurrency(Number(value));
                }}
              />
              <TableColumn
                fromKey="percentageUsage"
                header="Diferença"
                disableSorting
                width="25% !important"
                render={(percentage) => {
                  return (
                    <div className={getPercentageClass(percentage)}>
                      {Number(percentage).toFixed(2)}%
                    </div>
                  );
                }}
              />
            </Table>
            <Table
              dataKey="total"
              className="mt-s-200 mb-s-200"
              data={[totalSummary]}
              showColumnSelector={false}
            >
              <TableColumn
                fromKey="total"
                header="Total"
                disableSorting
                width="5px"
              />
              <TableColumn
                fromKey="category"
                header="Categoria"
                disableSorting
              />
              <TableColumn
                fromKey="businessPartner"
                header="Fornecedor"
                disableSorting
              />
              <TableColumn
                fromKey="totalBudget"
                header="Previsto"
                disableSorting
                render={(value) => {
                  return formatCurrency(Number(value));
                }}
              />
              <TableColumn
                fromKey="totalRealized"
                header="Realizado"
                disableSorting
                render={(value) => {
                  return formatCurrency(Number(value));
                }}
              />
              <TableColumn
                fromKey="percentageUsage"
                header="Diferença"
                render={(percentage) => {
                  return (
                    <div className={getPercentageClass(percentage)}>
                      {Number(percentage).toFixed(2)}%
                    </div>
                  );
                }}
                disableSorting
              />
            </Table>
          </div>
        )) || (
          <div className="mt-s-500">
            <div className="d-flex justify-center align-center">
              <h3>
                Deve-se pinar ao menos uma categoria para ver a evolução mensal.
              </h3>
            </div>
          </div>
        )}

        {categories.length > 1 && (
          <div className="mt-s-200">
            <nav>
              <div id="pagination-numbers">
                {!pages.includes(1) && (
                  <Tooltip text={categories[0].name}>
                    <button
                      onClick={() => setCurrentCategory(1)}
                      className="pagination-number bg-lightgray"
                    >
                      1
                    </button>
                  </Tooltip>
                )}
                {pages.map((i) => {
                  const buttonClassNames = [
                    'pagination-number',
                    'bg-lightgray',
                  ];

                  if (i === currentCategory) buttonClassNames.push('active');

                  return (
                    <Tooltip text={categories[i - 1].name}>
                      <button
                        onClick={() => setCurrentCategory(i)}
                        key={i}
                        className={buttonClassNames.join(' ')}
                      >
                        {i}
                      </button>
                    </Tooltip>
                  );
                })}
                {!pages.includes(categories.length) && (
                  <Tooltip text={categories[categories.length - 1].name}>
                    <button
                      onClick={() => setCurrentCategory(categories.length)}
                      className="pagination-number bg-lightgray"
                    >
                      {categories.length}
                    </button>
                  </Tooltip>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </>
  );
}
